import React from 'react';
import {
    ChartComponent,
    SeriesCollectionDirective,
    SeriesDirective,
    Inject,
    ColumnSeries,
    Category,
    Tooltip,
    DataLabel,
    Legend,
    Highlight
} from '@syncfusion/ej2-react-charts';
import { formatCurrency } from '../../utils/Format';

const MontantChart = ({ data, groupBy = 'Etat', title = '', height = '450px' }) => {
    const grouped = data.reduce((acc, curr) => {
        const key = curr[groupBy] || 'Non renseigné';
        const amount = curr.Montant_Du_Definitif || 0;
        acc[key] = (acc[key] || 0) + amount;
        return acc;
    }, {});

    const seriesData = Object.entries(grouped).map(([x, y]) => {
        const value = Math.round(y);
        return {
            x: x.length > 20 ? x.slice(0, 20) + '…' : x,
            y: value,
            text: formatCurrency(value),
            fill: value < 0 ? '#28a745' : '#FFA500'
        };
    });

    const yValues = seriesData.map(d => d.y);
    const rawMin = Math.min(0, ...yValues);
    const rawMax = Math.max(0, ...yValues);
    const range = rawMax - rawMin || 1; // éviter division par 0

    // Réduire la marge : on veut ~4 à 6 ticks max
    const approxTicks = 6;
    const roughInterval = range / approxTicks;

    // Trouve un intervalle "propre"
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughInterval)));
    let interval = Math.ceil(roughInterval / magnitude) * magnitude;

    // Si les valeurs sont très éloignées (ex : 1k et 5M), on adapte dynamiquement
    if (interval > 1_000_000 && rawMin > -100_000) {
        interval = Math.ceil(roughInterval / (magnitude * 10)) * magnitude;
    }

    // Calcule les bornes arrondies
    const minY = Math.floor(rawMin / interval) * interval;
    const maxY = Math.ceil(rawMax / interval) * interval;



    const onTooltipRender = (args) => {
        const val = args.point?.y ?? 0;
        args.text = `${args.point?.x} : <b>${formatCurrency(val)}</b>`;
    };

    const onAxisLabelRender = (args) => {
        const val = parseFloat(args.text.replace(/\s/g, '').replace(',', '.'));
        if (!isNaN(val)) {
            args.text = `${val.toLocaleString('fr-FR')} €`;
        }
    };

    return (
        <ChartComponent
            primaryXAxis={{
                valueType: 'Category',
                interval: 1,
                labelIntersectAction: 'Trim',
                majorGridLines: { width: 0 },
                majorTickLines: { width: 0 },
                labelStyle: { size: '11px' }
            }}
            primaryYAxis={{
                interval: interval,
                minimum: minY,
                maximum: maxY,

                labelStyle: { size: '11px' },
                majorTickLines: { width: 0 },
                lineStyle: { width: 0 },
                labelFormat: '{value} €'
            }}
            chartArea={{ border: { width: 0 }, margin: { bottom: 12 } }}
            title={title}
            tooltip={{ enable: true }}
            tooltipRender={onTooltipRender}
            axisLabelRender={onAxisLabelRender}
            height={height}
        >
            <Inject services={[ColumnSeries, Category, Tooltip, DataLabel, Legend, Highlight]} />
            <SeriesCollectionDirective>
                <SeriesDirective
                    dataSource={seriesData}
                    xName="x"
                    yName="y"
                    name="Montant"
                    type="Column"
                    columnSpacing={0.3}
                    cornerRadius={{ topLeft: 4, topRight: 4 }}
                    fill="#FFA500"
                    pointColorMapping="fill"
                    marker={{
                        dataLabel: {
                            visible: true,
                            position: 'Top',
                            name: 'text',
                            font: { color: '#000', size: '10px' }
                        }
                    }}
                />


            </SeriesCollectionDirective>
        </ChartComponent>
    );
};

export default MontantChart;
